/**
 * Settings and History, opened from the rail (PLAN.md decisions 6.5 and 6.6).
 *
 * Settings can be changed while a study is a draft and are fixed once it has run, so every version
 * in it stays comparable. History is the version tree: where each version came from, what changed,
 * how it did, and a way back to an earlier one - which changes what the next Ask runs, and deletes
 * nothing.
 */

import { CornerUpLeft, Lock } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useHistory, useSetWorkingVersion, useStudySettings, useUpdateSettings } from "@/lib/queries";

export function SettingsDialog({ studyId, onDone }: { studyId: string; onDone: () => void }) {
  const settings = useStudySettings(studyId);
  const update = useUpdateSettings(studyId);
  const [personas, setPersonas] = useState<number | null>(null);
  const [rounds, setRounds] = useState<number | null>(null);
  const view = settings.data;

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Settings</DialogTitle>
        <DialogDescription>What this study runs with.</DialogDescription>
      </DialogHeader>
      {!view ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-3 text-sm">
          {!view.editable && (
            <Alert>
              <Lock aria-hidden="true" />
              <AlertDescription>{view.locked_reason}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="settings-personas">Personas</Label>
            <Input
              id="settings-personas"
              type="number"
              min={10}
              max={view.max_personas}
              disabled={!view.editable}
              value={personas ?? view.personas}
              onChange={(e) => setPersonas(Number(e.target.value))}
            />
            <p className="text-xs text-muted-foreground">Between 10 and {view.max_personas} on your plan.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="settings-rounds">Rounds</Label>
            <Input
              id="settings-rounds"
              type="number"
              min={1}
              max={view.max_rounds}
              disabled={!view.editable}
              value={rounds ?? view.rounds}
              onChange={(e) => setRounds(Number(e.target.value))}
            />
            <p className="text-xs text-muted-foreground">
              Between 1 and {view.max_rounds}. More rounds means more chances for personas to change their minds
              after reading each other.
            </p>
          </div>
          <dl className="grid grid-cols-2 gap-1 text-xs text-muted-foreground">
            <dt>Model</dt>
            <dd className="text-foreground">{view.model}</dd>
            <dt>Mode</dt>
            <dd className="text-foreground">{view.mode}</dd>
            <dt>Validation floor</dt>
            <dd className="text-foreground">{view.validation_minimum} personas</dd>
            <dt>Flagged personas</dt>
            <dd className="text-foreground">{view.exclude_flagged ? "excluded from runs" : "still simulated"}</dd>
          </dl>
          {update.error && (
            <Alert variant="destructive">
              <AlertDescription>{(update.error as Error).message}</AlertDescription>
            </Alert>
          )}
        </div>
      )}
      <DialogFooter>
        {view?.editable && (
          <Button
            disabled={update.isPending}
            onClick={() =>
              update.mutate(
                { personas: personas ?? view.personas, rounds: rounds ?? view.rounds },
                {
                  onSuccess: () => {
                    toast.success("Settings saved");
                    onDone();
                  },
                },
              )
            }
          >
            Save
          </Button>
        )}
      </DialogFooter>
    </DialogContent>
  );
}

const SOURCES: Record<string, string> = {
  user: "written here",
  suggestion: "from Goal seek",
  edited_suggestion: "from Goal seek, edited",
  commitment_change: "a commitment change",
};

export function HistoryDialog({ studyId, onDone }: { studyId: string; onDone: () => void }) {
  const history = useHistory(studyId);
  const goBack = useSetWorkingVersion(studyId);
  const navigate = useNavigate();
  const versions = history.data?.versions ?? [];
  const depth = (id: string | null | undefined, seen = 0): number => {
    const parent = versions.find((v) => v.variant_id === id);
    return parent ? depth(parent.parent_id, seen + 1) : seen;
  };

  return (
    <DialogContent className="sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle>History</DialogTitle>
        <DialogDescription>
          Every version of the message, where it came from, and how it did. Going back to one changes what the next
          Ask runs; nothing is deleted.
        </DialogDescription>
      </DialogHeader>
      <div className="max-h-[60vh] space-y-2 overflow-y-auto">
        {versions.map((version) => (
          <article
            key={version.variant_id}
            className={cn(
              "rounded-lg border p-2 text-sm",
              version.is_working ? "border-step-done bg-step-done/10" : "border-white/10",
            )}
            style={{ marginLeft: depth(version.parent_id) * 16 }}
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{version.label}</span>
              <span className="text-xs text-muted-foreground">
                {version.variant_id} · {SOURCES[version.source] ?? version.source}
                {version.parent_id && ` · from ${version.parent_id}`}
                {version.parent_id && ` · +${version.words_added}/-${version.words_removed} words`}
              </span>
              {version.is_working && <span className="text-xs text-step-done">working version</span>}
              {!version.is_working && (
                <Button
                  size="xs"
                  variant="ghost"
                  className="ml-auto"
                  disabled={goBack.isPending}
                  onClick={() =>
                    goBack.mutate(version.variant_id, {
                      onSuccess: () => toast.success(`${version.label} is now the working version`),
                    })
                  }
                >
                  <CornerUpLeft aria-hidden="true" /> Go back to this
                </Button>
              )}
            </div>
            {version.commitment && (
              <p className="mt-1 text-xs text-step-next">
                Promises: {String((version.commitment as { new_locked_fact?: string }).new_locked_fact ?? "")}
              </p>
            )}
            <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
              {version.runs.length === 0 && <span>not run yet</span>}
              {version.runs.map((run) => (
                <button
                  key={run.run_id}
                  type="button"
                  className="underline-offset-2 hover:text-foreground hover:underline"
                  onClick={() => {
                    navigate(`/studies/${studyId}/runs/${run.run_id}`);
                    onDone();
                  }}
                >
                  {run.community === "holdout" ? "holdout · " : ""}
                  {run.headline ?? run.status}
                </button>
              ))}
            </div>
          </article>
        ))}
        {versions.length === 0 && <p className="text-sm text-muted-foreground">No versions yet.</p>}
      </div>
    </DialogContent>
  );
}
